// RankProgression: Shows user's rank journey and progress to next rank
import {
  getLevelRank,
  getNextRank,
  getRankProgress,
  getLevelsToNextRank,
  getAllRanks,
  formatRankDisplay,
} from "@/lib/rankSystem";

interface RankProgressionProps {
  level: number;
  xp?: number;
  xpToNextLevel?: number;
}

export function RankProgression({
  level,
  xp,
  xpToNextLevel,
}: RankProgressionProps) {
  const currentRank = getLevelRank(level);
  const nextRank = getNextRank(level);
  const rankProgress = getRankProgress(level);
  const levelsToNext = getLevelsToNextRank(level);
  const allRanks = getAllRanks();

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border-2 border-gray-200 dark:border-gray-700">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        Warrior Rank
      </h3>

      {/* Current Rank Display */}
      <div className="mb-6 p-4 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 rounded-lg border-2 border-gray-300 dark:border-gray-600">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-5xl">{currentRank.icon}</span>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Current Rank
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatRankDisplay(level)}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {currentRank.description}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Progress to Next Rank */}
      {nextRank && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Progress to {nextRank.name}
            </span>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {levelsToNext} level{levelsToNext !== 1 ? "s" : ""} remaining
            </span>
          </div>

          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
            <div
              className={`h-full bg-gradient-to-r from-${currentRank.color}-500 to-${currentRank.color}-600 transition-all duration-500 ease-out`}
              style={{ width: `${rankProgress}%` }}
            />
          </div>

          {/* Next Rank Preview */}
          <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 flex items-center gap-3">
            <span className="text-3xl opacity-50">{nextRank.icon}</span>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Next Rank
              </p>
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                {nextRank.name} (Level {nextRank.min})
              </p>
            </div>
          </div>
        </div>
      )}

      {/* XP Progress (if provided) */}
      {xp !== undefined && xpToNextLevel !== undefined && (
        <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              XP to Level {level + 1}
            </span>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {xp} / {xpToNextLevel}
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-yellow-400 to-yellow-600 transition-all duration-500 ease-out"
              style={{
                width: `${Math.min((xp / xpToNextLevel) * 100, 100)}%`,
              }}
            />
          </div>
        </div>
      )}

      {/* Rank Timeline */}
      <div>
        <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-3 uppercase tracking-wide">
          Rank Journey
        </p>
        <div className="space-y-2">
          {allRanks.map((rank) => {
            const isPast = level > rank.max;
            const isCurrent = level >= rank.min && level <= rank.max;

            return (
              <div
                key={rank.key}
                className={`flex items-center gap-3 p-2 rounded-lg transition-all ${
                  isCurrent
                    ? "bg-blue-100 dark:bg-blue-900/30 border-2 border-blue-400 dark:border-blue-600"
                    : isPast
                      ? "opacity-50"
                      : "opacity-30"
                }`}
              >
                <span className="text-2xl">{rank.icon}</span>
                <div className="flex-1">
                  <p
                    className={`text-sm font-semibold ${isCurrent ? "text-blue-900 dark:text-blue-100" : "text-gray-700 dark:text-gray-300"}`}
                  >
                    {rank.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Level {rank.min}-{rank.max}
                  </p>
                </div>
                {isCurrent && (
                  <span className="text-xs font-bold text-blue-600 dark:text-blue-400 bg-blue-200 dark:bg-blue-800 px-2 py-1 rounded">
                    Current
                  </span>
                )}
                {isPast && (
                  <span className="text-xs text-green-600 dark:text-green-400">
                    ✓
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
